import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import LanguageToggle from "@/components/LanguageToggle";
import { Lock, ArrowLeft, Mail, Send, CheckCircle2 } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();
  const { login } = useAuth();
  const adminMode = location.pathname.startsWith("/admin");
  const [mode, setMode] = useState("password"); // password | magic
  const [email, setEmail] = useState(adminMode ? "admin@example.com" : "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [magicSent, setMagicSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const u = await login(email, password);
      const dest =
        u?.role === "admin" ? "/admin" :
        u?.role === "attorney" ? "/attorney" :
        "/my-cases";
      navigate(dest, { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const sendMagic = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      await api.post("/auth/magic-request", { email });
      setMagicSent(true);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:flex flex-col justify-between p-12 text-white" style={{ background: "var(--brand)" }}>
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-white/70 hover:text-white" data-testid="login-back">
          <ArrowLeft className="h-4 w-4" /> {t("login.back")}
        </Link>
        <div>
          <div className="font-display text-4xl font-black tracking-tight">Apex MVA</div>
          <p className="mt-3 max-w-sm text-white/70">
            {adminMode ? t("login.tagline.admin") : t("login.tagline.claimant")}
          </p>
        </div>
        <div className="text-xs text-white/40">
          {adminMode ? t("login.footer.admin") : t("login.footer.claimant")}
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="card-soft w-full max-w-md p-8" data-testid="login-form">
          <div className="flex items-start justify-between gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-lg" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
              <Lock className="h-4 w-4 text-[#11322A]" />
            </div>
            <LanguageToggle variant="compact" />
          </div>
          <h1 className="mt-6 font-display text-2xl font-black tracking-tight text-[#11322A]">
            {adminMode ? t("login.title.admin") : t("login.title.claimant")}
          </h1>
          <p className="mt-1 text-sm text-[#5C6B68]">
            {adminMode ? t("login.subtitle.admin") : t("login.subtitle.claimant")}
          </p>

          {!adminMode && !magicSent && (
            <div className="mt-6 grid grid-cols-2 gap-1 rounded-lg bg-[#FDFBF7] p-1 text-xs font-bold uppercase tracking-[0.15em]">
              <button
                type="button"
                onClick={() => { setMode("password"); setErr(""); }}
                className={`rounded-md py-2 transition ${mode === "password" ? "bg-white text-[#11322A] shadow-sm" : "text-[#5C6B68] hover:text-[#11322A]"}`}
                data-testid="login-tab-password"
              >
                {t("magic.tab.password")}
              </button>
              <button
                type="button"
                onClick={() => { setMode("magic"); setErr(""); }}
                className={`rounded-md py-2 transition ${mode === "magic" ? "bg-white text-[#11322A] shadow-sm" : "text-[#5C6B68] hover:text-[#11322A]"}`}
                data-testid="login-tab-magic"
              >
                {t("magic.tab.magic")}
              </button>
            </div>
          )}

          {magicSent ? (
            <div className="mt-6 rounded-xl border border-[#bce5cc] bg-[#E6F6ED] p-5 text-sm text-[#166534]" data-testid="magic-sent">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                <div>
                  <div className="font-display text-base font-bold">{t("magic.sent.title")}</div>
                  <p className="mt-1 leading-relaxed">{t("magic.sent.body", { email })}</p>
                  <button
                    type="button"
                    onClick={() => { setMagicSent(false); }}
                    className="mt-4 text-xs font-bold uppercase tracking-[0.15em] underline hover:text-[#11322A]"
                    data-testid="magic-sent-again"
                  >
                    {t("magic.sent.again")}
                  </button>
                </div>
              </div>
            </div>
          ) : mode === "password" || adminMode ? (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="label-cap">{t("intake.email_label")}</label>
                <input type="email" className="input-field mt-2" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
              </div>
              <div>
                <label className="label-cap">{t("reg.password")}</label>
                <input type="password" className="input-field mt-2" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
              </div>
              {err && <div className="rounded-lg bg-[#FDE8E6] border border-[#F5B6B0] px-3 py-2 text-sm text-[#B91C1C]" data-testid="login-error">{err}</div>}
              <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50" data-testid="login-submit">
                {loading ? t("login.submitting") : t("login.submit")}
              </button>
            </form>
          ) : (
            <form onSubmit={sendMagic} className="mt-6 space-y-4">
              <p className="text-sm text-[#5C6B68]">{t("magic.subtitle")}</p>
              <div>
                <label className="label-cap">{t("intake.email_label")}</label>
                <div className="relative mt-2">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5C6B68]" />
                  <input
                    type="email"
                    className="input-field pl-10"
                    placeholder={t("magic.email_placeholder")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="magic-email"
                  />
                </div>
              </div>
              {err && <div className="rounded-lg bg-[#FDE8E6] border border-[#F5B6B0] px-3 py-2 text-sm text-[#B91C1C]" data-testid="magic-error">{err}</div>}
              <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50" data-testid="magic-submit">
                <Send className="h-4 w-4" /> {loading ? t("magic.sending") : t("magic.send")}
              </button>
            </form>
          )}

          {!adminMode && (
            <p className="mt-6 text-sm text-[#5C6B68]">
              {t("login.new_here")}{" "}
              <Link to="/signup" className="font-bold text-[#11322A] hover:text-[#E85D04]" data-testid="login-to-signup">
                {t("login.create_account")}
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
